import { Input } from "@chakra-ui/react";
import { useLayoutEffect, useCallback, useState } from "react";
import { RegisterOptions, useController, useWatch } from "react-hook-form";
import { toTimeWithoutSeconds, toTimeWithSeconds } from "helpers/trigger";
import { FLUSHED_FLOATING_INPUT_SX } from "definitions/constants/styling";
import { useFloatingField } from "./context";
import { sizeStyles } from "./sizeStyles";

type FloatingTimePickerProps = {
  control: any;
  name: string;
  rules?: RegisterOptions;
  defaultValue?: any;
  disabled?: boolean;
  onTimeChange?: (value: string) => void;
};

function FloatingTimePicker({ control, name, rules, defaultValue, disabled, onTimeChange }: FloatingTimePickerProps) {
  const { setHasValue, size } = useFloatingField();
  const { field } = useController({ control, name, rules, defaultValue });
  const styles = sizeStyles[size];

  const [isFocused, setIsFocused] = useState(false);
  const watchedValue = useWatch({ control, name });
  const displayValue = watchedValue ? toTimeWithoutSeconds(watchedValue) : "";

  useLayoutEffect(() => {
    setHasValue(!!watchedValue);
  }, [watchedValue, setHasValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formattedValue = toTimeWithSeconds(e.target.value);
      field.onChange(formattedValue);
      onTimeChange?.(formattedValue);
    },
    [field, onTimeChange],
  );

  return (
    <Input
      {...field}
      id={name}
      type="time"
      value={displayValue}
      onChange={handleChange}
      disabled={disabled}
      height={styles.inputHeight}
      paddingTop={styles.textInputPaddingTop}
      fontSize={styles.fontSize}
      variant="unstyled"
      sx={{
        ...FLUSHED_FLOATING_INPUT_SX,
        "&::-webkit-calendar-picker-indicator": {
          "margin-right": "16px",
          "margin-bottom": "12px",
          cursor: "pointer",
        },
      }}
      color={!watchedValue && !isFocused ? "transparent" : undefined}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      _hover={{ cursor: "pointer" }}
    />
  );
}

export default FloatingTimePicker;
